import { z } from "zod"

import { getOrganizationBearerHeaders } from "@/utils/auth/organization-token"

import type { NotebookRow } from "./notebookSeries"

const SLACK_BINDINGS_API_PATH = "/api/sofia/slack/bindings"
export const SLACK_AUDIENCE_POLICY_CONSENT_VERSION =
  "slack-channel-audience-v1" as const

const surfaceSchema = z.enum([
  "conversations",
  "daily_brief",
  "confirmed_actions",
  "notebook_ready",
])
export type SlackSurface = z.infer<typeof surfaceSchema>

const channelSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  is_private: z.boolean(),
  is_member: z.boolean(),
  is_shared: z.boolean(),
})
export type NotebookSlackChannel = z.infer<typeof channelSchema>

const bindingSchema = z.object({
  requires_rebind: z.boolean().optional(),
  channel_id: z.string().min(1),
  workspace_id: z.string().min(1),
  binding_version: z.number().int().positive(),
  enabled_surfaces: z.array(surfaceSchema),
  delivery_hour: z.number().int().min(0).max(23),
  timezone: z.string().min(1),
  allow_guest_conversations: z.boolean(),
  audience_policy_consent_version: z.literal(
    SLACK_AUDIENCE_POLICY_CONSENT_VERSION,
  ),
  shared_channel_consent: z.boolean(),
  is_shared: z.boolean(),
  bot_is_member: z.boolean(),
})
export type NotebookSlackBinding = z.infer<typeof bindingSchema>

const catalogSchema = z.object({
  organization_id: z.string().min(1),
  notebook_ready_available: z.boolean().optional(),
  binding_status: z.enum([
    "ready",
    "disconnected",
    "migration_required",
    "reauthorization_required",
  ]),
  channels: z.array(channelSchema),
  bindings: z.array(bindingSchema),
  next_channel_cursor: z.string().max(4096).regex(/^\S*$/).optional(),
})
export type NotebookSlackCatalog = z.infer<typeof catalogSchema>

const MAX_CHANNEL_CURSOR_PAGES = 10

export interface WorkspaceSlackChannel {
  id: string
  /** Null when the channel is not in the loaded Slack channel pages. */
  name: string | null
  notebookReady: boolean
}

type CatalogPageLoader = (
  cursor: string,
) => Promise<NotebookSlackCatalog | null>

function catalogPageLoader(
  organizationSlug: string,
  headers: Record<string, string>,
  signal?: AbortSignal,
): CatalogPageLoader {
  return async (cursor) => {
    const response = await fetch(
      `${SLACK_BINDINGS_API_PATH}/${encodeURIComponent(organizationSlug)}?channel_cursor=${encodeURIComponent(cursor)}`,
      {
        headers,
        signal,
        cache: "no-store",
      },
    )
    if (!response.ok) {
      if (
        response.status === 401 ||
        response.status === 403 ||
        response.status === 404
      ) {
        return null
      }
      throw new Error(
        "Could not load Slack channels. Close and reopen this dialog to retry.",
      )
    }
    const parsed = catalogSchema.safeParse(
      await response.json().catch(() => null),
    )
    return parsed.success ? parsed.data : null
  }
}

function mergeChannelPage(
  catalog: NotebookSlackCatalog,
  page: NotebookSlackCatalog,
): NotebookSlackCatalog {
  return {
    ...page,
    channels: [
      ...new Map(
        [...catalog.channels, ...page.channels].map((channel) => [
          channel.id,
          channel,
        ]),
      ).values(),
    ],
  }
}

/**
 * Follows the channel cursor for up to `pages` more pages. A failed page stops
 * the walk and leaves its cursor on the catalog, so callers can offer to load more.
 */
async function followChannelCursor(
  loadPage: CatalogPageLoader,
  catalog: NotebookSlackCatalog,
  pages: number,
): Promise<NotebookSlackCatalog> {
  let merged = catalog
  for (let loaded = 0; merged.next_channel_cursor && loaded < pages; loaded++) {
    let page: NotebookSlackCatalog | null = null
    try {
      page = await loadPage(merged.next_channel_cursor)
    } catch {
      break
    }
    if (!page) {
      break
    }
    merged = mergeChannelPage(merged, page)
  }
  return merged
}

/** A non-empty `next_channel_cursor` on the result means more channels remain. */
export async function fetchNotebookSlackCatalog(
  organizationSlug: string,
  signal?: AbortSignal,
): Promise<NotebookSlackCatalog | null> {
  const loadPage = catalogPageLoader(
    organizationSlug,
    await getOrganizationBearerHeaders(organizationSlug),
    signal,
  )
  const first = await loadPage("")
  return first
    ? followChannelCursor(loadPage, first, MAX_CHANNEL_CURSOR_PAGES - 1)
    : null
}

export async function loadMoreNotebookSlackChannels(
  organizationSlug: string,
  catalog: NotebookSlackCatalog,
  signal?: AbortSignal,
): Promise<NotebookSlackCatalog> {
  if (!catalog.next_channel_cursor) {
    return catalog
  }
  const loadPage = catalogPageLoader(
    organizationSlug,
    await getOrganizationBearerHeaders(organizationSlug),
    signal,
  )
  const page = await loadPage(catalog.next_channel_cursor).catch(() => null)
  if (!page) {
    throw new Error("Could not load more Slack channels. Try again.")
  }
  return followChannelCursor(
    loadPage,
    mergeChannelPage(catalog, page),
    MAX_CHANNEL_CURSOR_PAGES - 1,
  )
}

/** Null when the bindings are unknown: no catalog, or Slack is not ready. */
export function workspaceNotebookSlackChannels(
  catalog: NotebookSlackCatalog | null,
  workspaceId: string,
): WorkspaceSlackChannel[] | null {
  if (catalog?.binding_status !== "ready") {
    return null
  }
  const byId = new Map(catalog.channels.map((ch) => [ch.id, ch]))
  return catalog.bindings
    .filter((b) => b.workspace_id === workspaceId && !b.is_shared)
    .map((b) => ({
      id: b.channel_id,
      name: byId.get(b.channel_id)?.name ?? null,
      notebookReady: b.enabled_surfaces.includes("notebook_ready"),
    }))
}

/**
 * Names the channels it can and counts the rest: viewers without Slack admin
 * access never load channel names, and raw channel IDs mean nothing to them.
 */
function slackChannelsLabel(
  ids: string[],
  names: Map<string, string | null>,
): string {
  const known = ids.flatMap((id) => {
    const name = names.get(id)
    return name ? [`#${name}`] : []
  })
  const unknown = ids.length - known.length
  if (known.length === 0) {
    return `Slack: ${unknown} ${unknown === 1 ? "channel" : "channels"}`
  }
  return `Slack: ${known.join(", ")}${unknown > 0 ? ` +${unknown} more` : ""}`
}

export function notebookSlackSendLabel(
  row: NotebookRow,
  workspaceChannels: WorkspaceSlackChannel[] | null,
): string | null {
  if (row.kind !== "series" || !row.schedule) {
    return null
  }
  const selected = row.schedule.slack_channel_ids
  if (Array.isArray(selected) && selected.length === 0) {
    return "Slack off"
  }
  const channels = workspaceChannels ?? []
  const names = new Map(channels.map((ch) => [ch.id, ch.name]))
  const readyIds = channels.filter((ch) => ch.notebookReady).map((ch) => ch.id)
  if (Array.isArray(selected)) {
    // Sofia sends only to this workspace's bindings with notebook notifications
    // on. Without the bindings (non-admins), every saved channel is counted.
    const ready = new Set(readyIds)
    const active =
      workspaceChannels === null
        ? selected
        : selected.filter((id) => ready.has(id))
    return active.length === 0 ? "Slack off" : slackChannelsLabel(active, names)
  }
  return readyIds.length === 0 ? null : slackChannelsLabel(readyIds, names)
}

export async function saveWorkspaceChannelNotebookReady(args: {
  organizationSlug: string
  workspaceId: string
  channelId: string
  enableNotebookReady: boolean
  existing?: NotebookSlackBinding
}): Promise<NotebookSlackBinding> {
  const {
    organizationSlug,
    workspaceId,
    channelId,
    enableNotebookReady,
    existing,
  } = args
  const headers = await getOrganizationBearerHeaders(organizationSlug)
  const surfaces = new Set<SlackSurface>(existing?.enabled_surfaces ?? [])
  if (enableNotebookReady) {
    surfaces.add("notebook_ready")
  } else {
    surfaces.delete("notebook_ready")
  }
  const response = await fetch(
    `${SLACK_BINDINGS_API_PATH}/${encodeURIComponent(organizationSlug)}/${encodeURIComponent(channelId)}`,
    {
      method: surfaces.size === 0 && existing ? "DELETE" : "PUT",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify(
        surfaces.size === 0 && existing
          ? { expected_binding_version: existing.binding_version }
          : {
              workspace_id: workspaceId,
              enabled_surfaces: [...surfaces],
              delivery_hour: existing?.delivery_hour ?? 9,
              timezone: existing?.timezone ?? "America/Los_Angeles",
              allow_guest_conversations:
                existing?.allow_guest_conversations ?? false,
              audience_policy_consent_version:
                SLACK_AUDIENCE_POLICY_CONSENT_VERSION,
              shared_channel_consent: false,
              expected_binding_version: existing?.binding_version ?? null,
            },
      ),
    },
  )
  const payload: unknown = await response.json().catch(() => null)
  if (!response.ok) {
    const err = z
      .object({ error: z.object({ message: z.string() }) })
      .safeParse(payload)
    throw new Error(
      err.success
        ? err.data.error.message
        : "Could not update Slack channel notification settings.",
    )
  }
  if (surfaces.size === 0 && existing) {
    return { ...existing, enabled_surfaces: [] }
  }
  return z.object({ binding: bindingSchema }).parse(payload).binding
}
